import { useEffect, useRef, useState } from 'react';
import { Clock, LogOut } from 'lucide-react';
import { useCurrentUser, useLogout, authKeys } from '../lib/api/queries';
import { api } from '../lib/api/client';
import { onSessionActive } from '../lib/sessionActivity';
import type { CurrentUser } from '../lib/auth';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Watches the two deadlines that bound an authenticated session:
 *
 *   - <b>Idle timeout</b> (sliding): resets on every successful API call.
 *     The server's cookie has been renewed for another window; we mirror
 *     that locally by listening for sessionActivity events.
 *   - <b>Absolute timeout</b>: fixed at sign-in, server enforces an 8-hour
 *     hard cap regardless of activity. Comes off /me as an ISO timestamp.
 *
 * Two minutes before the sooner of those deadlines, renders a warning modal:
 * "Sign out in N seconds — stay signed in?". The button pings GET /me which
 * (a) extends the cookie via sliding and (b) returns a fresh absolute deadline
 * so the timers reset cleanly. If the user does nothing, the countdown hits
 * zero and we sign them out — the auth gate sees a null user and flips to
 * the login page.
 *
 * Mount this only inside the authenticated Layout so unauthenticated views
 * (LoginPage, MfaSetupPage) don't tick down.
 */
export function SessionExpiryWatcher() {
  const { data: user } = useCurrentUser();
  if (!user) return null;
  return <Inner user={user} />;
}

function Inner({ user }: { user: CurrentUser }) {
  const qc = useQueryClient();
  const logout = useLogout();

  // Two timers, sample the wall clock once per second when active. When the
  // sooner deadline gets within user.warningLeadSeconds we render the modal.
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const idleDeadlineRef = useRef<number>(idleDeadline(user.idleTimeoutSeconds));
  const absoluteDeadline = new Date(user.sessionAbsoluteExpiresAtUtc).getTime();
  const warningLeadSeconds = user.warningLeadSeconds;

  // Reset the idle timer whenever a successful API call slides the cookie.
  useEffect(() => {
    const unsub = onSessionActive(() => {
      idleDeadlineRef.current = idleDeadline(user.idleTimeoutSeconds);
    });
    return unsub;
  }, [user.idleTimeoutSeconds]);

  // Tick every second, compute remaining, decide whether to show the modal.
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const remaining = Math.floor(
        Math.min(idleDeadlineRef.current, absoluteDeadline) / 1000 - now / 1000,
      );

      if (remaining <= 0) {
        // Session is over. Sign out client-side and let the auth gate flip.
        // (The server's cookie is already invalid — POST /logout will 401
        // but that's fine; we just want the local cache cleared.)
        setSecondsRemaining(null);
        logout.mutate();
        return;
      }

      setSecondsRemaining(remaining <= warningLeadSeconds ? remaining : null);
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [absoluteDeadline, warningLeadSeconds, logout]);

  const onStaySignedIn = async () => {
    try {
      // GET /me extends the cookie (sliding) and returns the fresh user
      // payload including a re-issued sessionAbsoluteExpiresAtUtc if the
      // server happens to consider this a new sign-in. Either way, the
      // sessionActivity hook resets the idle countdown.
      const fresh = await api<CurrentUser>('/api/auth/me');
      qc.setQueryData(authKeys.me, fresh);
      setSecondsRemaining(null);
    } catch {
      // /me failed — most likely the session already expired. Sign out
      // to flip the gate.
      logout.mutate();
    }
  };

  if (secondsRemaining === null) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-lg max-w-sm w-full p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="w-6 h-6 text-warning-500" aria-hidden />
          <h2 className="text-lg font-semibold text-gray-900">
            Still there?
          </h2>
        </div>
        <p className="text-sm text-gray-700">
          You'll be signed out in <strong>{secondsRemaining}</strong>{' '}
          second{secondsRemaining === 1 ? '' : 's'} for inactivity.
          Any in-progress form data will be lost.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onStaySignedIn}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2
                       bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors"
          >
            Stay signed in
          </button>
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2
                       border border-gray-300 text-gray-700 rounded-md
                       hover:bg-gray-50 transition-colors"
          >
            <LogOut className="w-4 h-4" aria-hidden />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function idleDeadline(idleTimeoutSeconds: number): number {
  return Date.now() + idleTimeoutSeconds * 1000;
}
